/// <amd-module name="Environments/EnvironmentTraceabilityChanges/Components/CommitsComponent" />
import React = require("react");
import ReactDOM = require("react-dom");

import { CommitsActionsCreator } from "Environments/EnvironmentTraceabilityChanges/ActionsCreators/CommitsActionsCreator";
import { CommitsStore } from "Environments/EnvironmentTraceabilityChanges/Stores/CommitsStore";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import * as Resources from "Environments/EnvironmentTraceabilityChanges/Resources";
import * as Constants from "Environments/EnvironmentTraceabilityChanges/Common/Constants"
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";
import { EnvironmentCommitsRecord } from "Environments/RemoteClients/Contracts/Traceability";

import { format } from "VSS/Core/Util/String";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, IVssComponentProperties, VssComponent} from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { IVCNavigationService } from "VC/Common/Types/Navigation";

import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { FilterBar } from "VSSUI/FilterBar";
import { ITableColumn, ITableRow, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { Filter, FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { VssPersona } from "VSSUI/VssPersona";
import { Tooltip } from "VSSUI/TooltipEx";
import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";
import { Area, FeatureNames, Action } from "Environments/EnvironmentTraceabilityChanges/Common/Utils";

interface ICommitsTableItem extends EnvironmentCommitsRecord {}

export interface ICommitsProperties extends IVssComponentProperties {
  instanceId: string;
  environmentId: number;
  environmentExecutionRecordId: number;
  ownerId: number;
}

export interface IState extends IEnvironmentBaseComponentState {
  itemProvider: ArrayItemProvider<ICommitsTableItem>;
}

class CommitsComponent extends EnvironmentBaseComponent<ICommitsProperties, IState> {

  public getInstanceId(context: IVssComponentContext): string {
    return "commits-instance-id-" + this.componentId;
  }

  public constructor(props: ICommitsProperties, context: IVssComponentContext) {
    super(props, context);

    this._actionCreator = ActionCreatorManager.CreateActionCreator<CommitsActionsCreator>(CommitsActionsCreator, this._instanceId);
    this._store = StoreManager.CreateStore<CommitsStore>(CommitsStore, this._instanceId);
    this._actionCreator.getCommits(this.context.pageContext);
    publishTelemetry(context.pageContext, Area, FeatureNames.EnvironmentRunDetailsView, { "Action": Action.CommitTabNavigation, "EnvironmentId": props.environmentId.toString(), "EnvironmentExecutionRecordId": props.environmentExecutionRecordId.toString(), "OwnerId": props.ownerId.toString()});
    
    this.state = {
      itemProvider: this._getItemProvider(),
      errorMessages: {}
    }

    this._store.addChangedListener(this._onStoreChanged);
  }

  public render(): JSX.Element {
    const commitsProvider = this.state.itemProvider;

    return (
      <div className="page-content page-content-top">
        <Card className="bolt-card-no-vertical-padding" contentProps={{contentPadding: false}}>
          <div>
            <Table<ICommitsTableItem>
              ariaLabel={Resources.CommitColumnName}
              className="commits-table"
              key={commitsProvider.length}
              columns={this._sizableColumns}
              itemProvider={commitsProvider}
              onActivate={this._onRowClicked}
            />
          </div>
        </Card>
      </div>
    );
  }

  private _onStoreChanged = (): void => {
    this.setState({
        itemProvider: this._getItemProvider()
    });
  };

  private _getItemProvider() : ArrayItemProvider<ICommitsTableItem> {
    const commits = this._store.getCommits();
    return new ArrayItemProvider<ICommitsTableItem>(commits.map<ICommitsTableItem>(change => {
      return {
        id: change.id,
        message: change.message,
        author: change.author,
        displayUri: change.displayUri,
        timestamp: change.timestamp
      } as ICommitsTableItem;
    }));
  }

  private _renderShaColumn = ( rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICommitsTableItem>, tableItem: ICommitsTableItem ): JSX.Element => {
    return (
      <SimpleTableCell
        key={"commits-col-" + columnIndex}
        ariaLabel={"commits-col-" + columnIndex}
        className={"font-size-ms monospaced-text"}
        columnIndex={columnIndex}
        tableColumn={tableColumn}
        children={tableItem.id.substr(0, 9)}
      />
    );
  }

  private _renderCommitColumn = ( rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICommitsTableItem>, tableItem: ICommitsTableItem ): JSX.Element => {
    return (
      <TwoLineTableCell 
        className="no-v-padding"
        key={"commits-col-" + columnIndex}
        columnIndex={columnIndex}
        tableColumn={tableColumn}
        line1={
          <Tooltip overflowOnly={true} text={tableItem.message}>
            <span className={"font-weight-semibold body-m text-ellipsis"}>{tableItem.message}</span>
          </Tooltip>
        }
        line2={
          <div className="flex-row" >          
            <VssPersona
              size={"extra-small-plus"}
              className="commit-persona-margin"
              identityDetailsProvider={{
                getIdentityImageUrl: (size: number): string => {
                return tableItem.author && tableItem.author._links && tableItem.author._links.avatar && tableItem.author._links.avatar.href;
                },
                getDisplayName: (): string => {
                return tableItem.author.displayName;
                }
              }}
            />
            <div className={"font-size secondary-text flex-row flex-center"}>
              <span>{format(Resources.AuthoredFormat, tableItem.author.displayName)}&nbsp;</span>
              {tableItem.timestamp && (
                <Ago format={AgoFormat.Compact} date={tableItem.timestamp} />
              )}
            </div>
          </div>
        }
      />
    );
  };

  private _onSizeSizable = ( event: MouseEvent, index: number, width: number ): void => {
    (this._sizableColumns[index].width as ObservableValue<number>).value = width;
  };

  private _onRowClicked = (event: any, row: ITableRow<ICommitsTableItem>): void => {
    var repo = this._store.getRepoInfo();
    this.vcNavigationService.then((service) => {
      onClickFPS(this.context.pageContext, row.data.displayUri || service.getGitCommitUrl(row.data.id, ( repo && repo.name ) || ""), true);
    });
  }

  private _sizableColumns = [
    {
      className: "commit-description-cell",
      id: "commit",
      name: Resources.CommitColumnName,
      minWidth: 300,
      width: new ObservableValue(500),
      renderCell: this._renderCommitColumn,
      onSize: this._onSizeSizable
    },
    {
      id: "sha",
      name: Resources.ShaColumnName,
      minWidth: 100,
      width: new ObservableValue(-100),
      renderCell: this._renderShaColumn,
      onSize: this._onSizeSizable
    }
  ];

  private get vcNavigationService(): Promise<IVCNavigationService> {
    if (!this._vcNavigationService) {
      this._vcNavigationService = this._vcContributionPromise.then(() => {
        return this.context.pageContext.getService<IVCNavigationService>("IVCNavigationService");
      });
    }

    return this._vcNavigationService;
  }

  public componentDidMount(): void {
    this._actionCreator.getCommitsAsync(this.context.pageContext);
    super.componentDidMount();

    // Start fetching vc-common content asynchronously
    const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
    this._vcContributionPromise = contributionService.getContributionsAsync<any>(["ms.vss-code-web.common-content"]);
  }

  public componentWillUnmount(): void {
    this._store.removeChangedListener(this._onStoreChanged);
    StoreManager.DeleteStore<CommitsStore>(CommitsStore, this._instanceId);
    ActionCreatorManager.DeleteActionCreator<CommitsActionsCreator>(CommitsActionsCreator, this._instanceId);
    super.componentWillUnmount();
  }
  
  private _store: CommitsStore;
  private _actionCreator: CommitsActionsCreator;
  private _vcContributionPromise: Promise<any>;
  private _vcNavigationService: Promise<IVCNavigationService> | undefined;
}

VssComponent.register("commitsView", CommitsComponent);
